/**
 * lib/experiences/visuals.ts  ·  Real Images Layer V1.1
 * ─────────────────────────────────────────────────────────────────────────────
 * Curated visual metadata for the Experiences feature.
 *
 * Sources    : Unsplash stable CDN  (no API key required)
 * URL format : https://images.unsplash.com/photo-{id}?auto=format&…
 * Fallback   : all consumers MUST implement onError — see CityHero and
 *              ImagePlaceholder in routes/experiences.tsx.
 *
 * Public surface:
 *   unsplashUrl(id, w, h?, q?)    — build a stable CDN URL
 *   CITY_VISUALS                  — 25 cities keyed by IATA
 *   CATEGORY_VISUALS              — 20 categories
 *   getCityVisual(cityOrIata)     — lookup by IATA / slug / EN / ES name
 *   getCategoryVisual(category)   — lookup with Spanish-alias support
 */

// ─── URL helper ───────────────────────────────────────────────────────────────

/**
 * Build a stable Unsplash CDN URL.
 * `id` is the `photo-{id}` fragment from the Unsplash page URL.
 */
export function unsplashUrl(id: string, w: number, h?: number, q = 80): string {
  const dims = h ? `w=${w}&h=${h}&fit=crop` : `w=${w}`;
  return `https://images.unsplash.com/photo-${id}?auto=format&${dims}&q=${q}`;
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CityVisual {
  /** 1 400 px wide landscape — load="eager" for hero */
  heroImage:       string;
  /** 600 × 400 crop — load="lazy" for thumbnails */
  thumbnailImage:  string;
  /** Short descriptive caption for alt text */
  caption:         string;
  /** Photo credit */
  attribution:     string;
  /** CSS object-position for the hero image (default "center center") */
  objectPosition?: string;
  /**
   * City-specific colour hints.
   * `overlay`  — dark scrim tint for hero readability (oklch)
   * `accent`   — highlight / badge colour (oklch) — forward-use only
   */
  palette: {
    overlay: string;
    accent:  string;
  };
}

export interface CategoryVisual {
  /** 400 × 400 square — load="lazy" */
  image:       string;
  attribution: string;
}

// ─── City visuals — 25 cities ─────────────────────────────────────────────────
// Existing IDs (V1.0) are preserved exactly.

export const CITY_VISUALS: Readonly<Record<string, CityVisual>> = {

  // ── Asia ──────────────────────────────────────────────────────────────────

  TYO: {
    heroImage:      unsplashUrl("1540959733332-eab4deabeeaf", 1400),
    thumbnailImage: unsplashUrl("1540959733332-eab4deabeeaf", 600, 400),
    caption:        "Shinjuku, Tokio",
    attribution:    "Unsplash",
    palette: { overlay: "oklch(0.06 0.06 278)", accent: "oklch(0.68 0.18 285)" },
  },

  BKK: {
    heroImage:      unsplashUrl("1508009603885-50cf7c579365", 1400),
    thumbnailImage: unsplashUrl("1508009603885-50cf7c579365", 600, 400),
    caption:        "Wat Arun, Bangkok",
    attribution:    "Unsplash",
    palette: { overlay: "oklch(0.06 0.03 62)", accent: "oklch(0.70 0.16 65)" },
  },

  // ── Middle East / Eurasia ─────────────────────────────────────────────────

  IST: {
    heroImage:      unsplashUrl("1524231757912-21f4fe3a7200", 1400),
    thumbnailImage: unsplashUrl("1524231757912-21f4fe3a7200", 600, 400),
    caption:        "Mezquita Azul, Estambul",
    attribution:    "Unsplash",
    palette: { overlay: "oklch(0.06 0.03 48)", accent: "oklch(0.65 0.14 52)" },
  },

  // ── Western Europe ────────────────────────────────────────────────────────

  CDG: {
    heroImage:      unsplashUrl("1502602898657-3e91760cbb34", 1400),
    thumbnailImage: unsplashUrl("1502602898657-3e91760cbb34", 600, 400),
    caption:        "Torre Eiffel, París",
    attribution:    "Unsplash",
    palette: { overlay: "oklch(0.06 0.03 238)", accent: "oklch(0.55 0.11 238)" },
  },

  MAD: {
    heroImage:      unsplashUrl("1543783207-ec64e4d95325", 1400),
    thumbnailImage: unsplashUrl("1543783207-ec64e4d95325", 600, 400),
    caption:        "Gran Vía, Madrid",
    attribution:    "Unsplash",
    palette: { overlay: "oklch(0.06 0.04 27)", accent: "oklch(0.62 0.19 27)" },
  },

  BCN: {
    heroImage:      unsplashUrl("1539037116277-4db20889f2d4", 1400),
    thumbnailImage: unsplashUrl("1539037116277-4db20889f2d4", 600, 400),
    caption:        "Barcelona",
    attribution:    "Unsplash",
    palette: { overlay: "oklch(0.06 0.03 46)", accent: "oklch(0.72 0.14 50)" },
  },

  LIS: {
    heroImage:      unsplashUrl("1555881400-74d7acaacd8b", 1400),
    thumbnailImage: unsplashUrl("1555881400-74d7acaacd8b", 600, 400),
    caption:        "Alfama, Lisboa",
    attribution:    "Unsplash",
    palette: { overlay: "oklch(0.06 0.03 32)", accent: "oklch(0.68 0.13 32)" },
  },

  LHR: {
    heroImage:      unsplashUrl("1513635269975-59663e0ac1ad", 1400),
    thumbnailImage: unsplashUrl("1513635269975-59663e0ac1ad", 600, 400),
    caption:        "Westminster, Londres",
    attribution:    "Unsplash",
    palette: { overlay: "oklch(0.06 0.015 220)", accent: "oklch(0.50 0.08 220)" },
  },

  AMS: {
    heroImage:      unsplashUrl("1534351590666-13e3e96b5017", 1400),
    thumbnailImage: unsplashUrl("1534351590666-13e3e96b5017", 600, 400),
    caption:        "Canales, Ámsterdam",
    attribution:    "Unsplash",
    palette: { overlay: "oklch(0.06 0.025 198)", accent: "oklch(0.54 0.10 195)" },
  },

  BER: {
    heroImage:      unsplashUrl("1560969184-10fe8719e047", 1400),
    thumbnailImage: unsplashUrl("1560969184-10fe8719e047", 600, 400),
    caption:        "Puerta de Brandeburgo, Berlín",
    attribution:    "Unsplash",
    palette: { overlay: "oklch(0.06 0.010 188)", accent: "oklch(0.48 0.06 190)" },
  },

  // ── Japan (multi-city V1) ─────────────────────────────────────────────────

  KYO: {
    heroImage:      unsplashUrl("1493976040374-85c8e12f0c0e", 1400),
    thumbnailImage: unsplashUrl("1493976040374-85c8e12f0c0e", 600, 400),
    caption:        "Fushimi Inari, Kioto",
    attribution:    "Unsplash",
    palette: { overlay: "oklch(0.06 0.04 356)", accent: "oklch(0.62 0.14 358)" },
  },
  OSA: {
    heroImage:      unsplashUrl("1590559899731-a382839e5549", 1400),
    thumbnailImage: unsplashUrl("1590559899731-a382839e5549", 600, 400),
    caption:        "Dotonbori, Osaka",
    attribution:    "Unsplash",
    palette: { overlay: "oklch(0.06 0.03 48)", accent: "oklch(0.70 0.16 52)" },
  },

  // ── Portugal (multi-city V1) ─────────────────────────────────────────────

  OPO: {
    heroImage:      unsplashUrl("1558638027-0cf14bed5f28", 1400),
    thumbnailImage: unsplashUrl("1558638027-0cf14bed5f28", 600, 400),
    caption:        "Ribeira, Oporto",
    attribution:    "Unsplash",
    palette: { overlay: "oklch(0.06 0.03 28)", accent: "oklch(0.65 0.13 28)" },
  },

  // ── Spain / Andalusia (multi-city V1) ────────────────────────────────────

  SEV: {
    heroImage:      unsplashUrl("1558642891-54be180ea339", 1400),
    thumbnailImage: unsplashUrl("1558642891-54be180ea339", 600, 400),
    caption:        "Plaza de España, Sevilla",
    attribution:    "Unsplash",
    palette: { overlay: "oklch(0.06 0.04 44)", accent: "oklch(0.68 0.18 48)" },
  },
  GRA: {
    heroImage:      unsplashUrl("1554629947-334ff61d29fc", 1400),
    thumbnailImage: unsplashUrl("1554629947-334ff61d29fc", 600, 400),
    caption:        "La Alhambra, Granada",
    attribution:    "Unsplash",
    palette: { overlay: "oklch(0.06 0.025 148)", accent: "oklch(0.56 0.14 148)" },
  },

  // ── Italy (multi-city V1) ─────────────────────────────────────────────────

  VCE: {
    heroImage:      unsplashUrl("1523906921341-71c7d24e2a0f", 1400),
    thumbnailImage: unsplashUrl("1523906921341-71c7d24e2a0f", 600, 400),
    caption:        "Gran Canal, Venecia",
    attribution:    "Unsplash",
    palette: { overlay: "oklch(0.06 0.025 206)", accent: "oklch(0.62 0.14 206)" },
  },
  // ROM maps to same photo as FCO (both are Rome)
  ROM: {
    heroImage:       unsplashUrl("1552832230-c0197dd311b5", 1400),
    thumbnailImage:  unsplashUrl("1552832230-c0197dd311b5", 600, 400),
    caption:         "Coliseo Romano, Roma",
    attribution:     "Unsplash",
    objectPosition:  "center 45%",
    palette: { overlay: "oklch(0.06 0.025 40)", accent: "oklch(0.65 0.14 42)" },
  },

  // ── Southern Europe ───────────────────────────────────────────────────────

  FCO: {
    heroImage:       unsplashUrl("1552832230-c0197dd311b5", 1400),
    thumbnailImage:  unsplashUrl("1552832230-c0197dd311b5", 600, 400),
    caption:         "Coliseo, Roma",
    attribution:     "Unsplash",
    objectPosition:  "center 45%",
    palette: { overlay: "oklch(0.06 0.025 40)", accent: "oklch(0.65 0.14 42)" },
  },

  MXP: {
    heroImage:      unsplashUrl("1556909114-f6e7ad7d3136", 1400),
    thumbnailImage: unsplashUrl("1556909114-f6e7ad7d3136", 600, 400),
    caption:        "Duomo, Milán",
    attribution:    "Unsplash",
    palette: { overlay: "oklch(0.06 0.015 235)", accent: "oklch(0.52 0.08 235)" },
  },

  FLR: {
    heroImage:      unsplashUrl("1543429258-afbadcc93b20", 1400),
    thumbnailImage: unsplashUrl("1543429258-afbadcc93b20", 600, 400),
    caption:        "Duomo, Florencia",
    attribution:    "Unsplash",
    palette: { overlay: "oklch(0.06 0.025 50)", accent: "oklch(0.68 0.14 52)" },
  },

  // ── North America ─────────────────────────────────────────────────────────

  NYC: {
    heroImage:      unsplashUrl("1522083165195-3424ed129620", 1400),
    thumbnailImage: unsplashUrl("1522083165195-3424ed129620", 600, 400),
    caption:        "Manhattan, Nueva York",
    attribution:    "Unsplash",
    palette: { overlay: "oklch(0.06 0.025 258)", accent: "oklch(0.56 0.12 258)" },
  },

  MIA: {
    heroImage:      unsplashUrl("1506905925346-21bda4d32df4", 1400),
    thumbnailImage: unsplashUrl("1506905925346-21bda4d32df4", 600, 400),
    caption:        "South Beach, Miami",
    attribution:    "Unsplash",
    palette: { overlay: "oklch(0.06 0.025 188)", accent: "oklch(0.65 0.14 188)" },
  },

  // ── Mexico & Central America ──────────────────────────────────────────────

  MEX: {
    heroImage:      unsplashUrl("1518105779142-d975f22f1b0a", 1400),
    thumbnailImage: unsplashUrl("1518105779142-d975f22f1b0a", 600, 400),
    caption:        "Centro Histórico, Ciudad de México",
    attribution:    "Unsplash",
    palette: { overlay: "oklch(0.06 0.03 52)", accent: "oklch(0.72 0.16 52)" },
  },

  // ── South America ─────────────────────────────────────────────────────────

  EZE: {
    heroImage:      unsplashUrl("1589909202802-8f4aadce1849", 1400),
    thumbnailImage: unsplashUrl("1589909202802-8f4aadce1849", 600, 400),
    caption:        "Buenos Aires",
    attribution:    "Unsplash",
    palette: { overlay: "oklch(0.06 0.025 54)", accent: "oklch(0.64 0.12 54)" },
  },

  BOG: {
    heroImage:      unsplashUrl("1534758607-3ace3e2c8941", 1400),
    thumbnailImage: unsplashUrl("1534758607-3ace3e2c8941", 600, 400),
    caption:        "La Candelaria, Bogotá",
    attribution:    "Unsplash",
    palette: { overlay: "oklch(0.06 0.025 148)", accent: "oklch(0.52 0.12 148)" },
  },

  MDE: {
    heroImage:      unsplashUrl("1558618666-fcd25c85cd64", 1400),
    thumbnailImage: unsplashUrl("1558618666-fcd25c85cd64", 600, 400),
    caption:        "Medellín",
    attribution:    "Unsplash",
    palette: { overlay: "oklch(0.06 0.025 145)", accent: "oklch(0.58 0.14 142)" },
  },

  CTG: {
    heroImage:      unsplashUrl("1583275478398-13c95bbb53f5", 1400),
    thumbnailImage: unsplashUrl("1583275478398-13c95bbb53f5", 600, 400),
    caption:        "Ciudad Amurallada, Cartagena",
    attribution:    "Unsplash",
    palette: { overlay: "oklch(0.06 0.025 48)", accent: "oklch(0.68 0.16 48)" },
  },

  LIM: {
    heroImage:      unsplashUrl("1531065208531-4036c0dba3ca", 1400),
    thumbnailImage: unsplashUrl("1531065208531-4036c0dba3ca", 600, 400),
    caption:        "Miraflores, Lima",
    attribution:    "Unsplash",
    palette: { overlay: "oklch(0.06 0.018 212)", accent: "oklch(0.54 0.10 212)" },
  },

  CUZ: {
    heroImage:      unsplashUrl("1526392060635-9d6019884377", 1400),
    thumbnailImage: unsplashUrl("1526392060635-9d6019884377", 600, 400),
    caption:        "Plaza de Armas, Cusco",
    attribution:    "Unsplash",
    palette: { overlay: "oklch(0.06 0.025 36)", accent: "oklch(0.56 0.14 38)" },
  },

  GIG: {
    heroImage:      unsplashUrl("1483729558449-99ef09a8c325", 1400),
    thumbnailImage: unsplashUrl("1483729558449-99ef09a8c325", 600, 400),
    caption:        "Cristo Redentor, Río de Janeiro",
    attribution:    "Unsplash",
    palette: { overlay: "oklch(0.06 0.025 52)", accent: "oklch(0.68 0.14 52)" },
  },

  GRU: {
    heroImage:      unsplashUrl("1605216663980-b7a22cbc9f67", 1400),
    thumbnailImage: unsplashUrl("1605216663980-b7a22cbc9f67", 600, 400),
    caption:        "Avenida Paulista, São Paulo",
    attribution:    "Unsplash",
    palette: { overlay: "oklch(0.06 0.012 222)", accent: "oklch(0.50 0.08 222)" },
  },

  SCL: {
    heroImage:      unsplashUrl("1575540015718-efe1e0d15cda", 1400),
    thumbnailImage: unsplashUrl("1575540015718-efe1e0d15cda", 600, 400),
    caption:        "Santiago de Chile",
    attribution:    "Unsplash",
    palette: { overlay: "oklch(0.06 0.018 232)", accent: "oklch(0.52 0.10 235)" },
  },
};

// ─── Category visuals — 20 categories ────────────────────────────────────────
// Existing IDs (V1.0) are preserved exactly.

export const CATEGORY_VISUALS: Readonly<Record<string, CategoryVisual>> = {

  // ── V1.0 (10) ─────────────────────────────────────────────────────────────

  food: {
    image:       unsplashUrl("1414235077428-338989a2e8c0", 400, 400),
    attribution: "Unsplash",
  },
  nightlife: {
    image:       unsplashUrl("1516450360452-9312f5e86fc7", 400, 400),
    attribution: "Unsplash",
  },
  culture: {
    // Marketplace Image Quality V2: the previous ID here
    // (1568702846914-96b305d2aaeb) was a red apple on a plain background — a
    // real production bug (a Madrid / Santiago Bernabéu tour rendered fruit
    // as its fallback image). Replaced with a verified museum-interior photo.
    image:       unsplashUrl("1774514580599-c3dae376348e", 400, 400),
    attribution: "Unsplash",
  },
  art: {
    image:       unsplashUrl("1561214115-f2f134cc4912", 400, 400),
    attribution: "Unsplash",
  },
  nature: {
    image:       unsplashUrl("1441974231531-c6227db76b6e", 400, 400),
    attribution: "Unsplash",
  },
  music: {
    image:       unsplashUrl("1493225457124-a3eb161ffa5f", 400, 400),
    attribution: "Unsplash",
  },
  architecture: {
    image:       unsplashUrl("1486325212027-8081e485255e", 400, 400),
    attribution: "Unsplash",
  },
  wellness: {
    image:       unsplashUrl("1544367567-0f2fcb009e0b", 400, 400),
    attribution: "Unsplash",
  },
  shopping: {
    image:       unsplashUrl("1481437156560-3205f6a55735", 400, 400),
    attribution: "Unsplash",
  },
  // Tokyo neon street — fits anime subculture aesthetic
  anime: {
    image:       unsplashUrl("1528360983277-13d401cdc186", 400, 400),
    attribution: "Unsplash",
  },

  // ── V1.1 (10 new) ─────────────────────────────────────────────────────────

  // Romantic — couple at golden-hour coastal viewpoint
  romantic: {
    image:       unsplashUrl("1516589178581-6cd7833ae3b2", 400, 400),
    attribution: "Unsplash",
  },
  // Markets — colourful covered street market
  // V2: previous ID (1555939594-58d7cb561ad1) was a grilled-meat platter, not
  // a market scene. Replaced with a verified street of market stalls.
  markets: {
    image:       unsplashUrl("1754638335233-e29065d73bb4", 400, 400),
    attribution: "Unsplash",
  },
  // Cafés — latte art in a light-flooded café
  cafes: {
    image:       unsplashUrl("1495474472287-4d71bcdd2085", 400, 400),
    attribution: "Unsplash",
  },
  // Family — family at beach destination
  // V2: previous ID (1476514525535-07fb3b4ae5f1) showed a couple
  // photographing hot-air balloons — no family in frame. Replaced with a
  // verified family silhouette at a beach.
  family: {
    image:       unsplashUrl("1576696058573-12b47c49559e", 400, 400),
    attribution: "Unsplash",
  },
  // Hidden gems — narrow cobblestone alley
  // V2: previous ID (1519517191396-f35e4b11c982) 404s — a broken image that
  // never loaded. Replaced with a verified narrow old-town alley.
  hidden_gems: {
    image:       unsplashUrl("1757949087011-cfab519ca1f6", 400, 400),
    attribution: "Unsplash",
  },
  // Viewpoints — person at hilltop panoramic viewpoint
  viewpoints: {
    image:       unsplashUrl("1530789253388-582c481c54b0", 400, 400),
    attribution: "Unsplash",
  },
  // Local life — everyday street scene / neighbourhood
  // V2: previous ID (1530026186672-2cd00ffc50fe) was an anatomical heart
  // model on a medical textbook — completely unrelated. Replaced with a
  // verified residential street scene.
  local_life: {
    image:       unsplashUrl("1761427144711-31115f96660e", 400, 400),
    attribution: "Unsplash",
  },
  // Design — modern interior / contemporary architecture
  design: {
    image:       unsplashUrl("1555041469-a586c61ea9bc", 400, 400),
    attribution: "Unsplash",
  },
  // History — ancient ruins or heritage architecture
  // V2: previous ID (1555215695-3004980ad54e) was a white BMW sports car on a
  // palm-lined street — unrelated to "history". Replaced with verified
  // ancient-ruin arches.
  history: {
    image:       unsplashUrl("1747413876636-b10f959845d9", 400, 400),
    attribution: "Unsplash",
  },
  // Sports — outdoor adventure / urban sport
  sports: {
    image:       unsplashUrl("1571019614242-c5c5dee9f50b", 400, 400),
    attribution: "Unsplash",
  },
  // Stadium — sports-venue visual intent (Marketplace Image Quality V2).
  // Not a backend category (exp.category never equals "stadium"); only
  // reached via resolveVisualIntent() below, e.g. a "culture"-tagged
  // Bernabéu/stadium tour that would otherwise render a generic museum image.
  stadium: {
    image:       unsplashUrl("1780746380602-4f57921429f6", 400, 400),
    attribution: "Unsplash",
  },
};

// ─── City lookup helpers ──────────────────────────────────────────────────────
// All keys are lowercase; getCityVisual normalises its input before lookup.

const CITY_SLUG_TO_IATA: Readonly<Record<string, string>> = {
  // ── IATA codes ──
  "tyo": "TYO", "bcn": "BCN", "lis": "LIS", "cdg": "CDG",
  "mex": "MEX", "mad": "MAD", "fco": "FCO", "lhr": "LHR",
  "ams": "AMS", "ber": "BER", "mxp": "MXP", "flr": "FLR",
  "nyc": "NYC", "mia": "MIA", "eze": "EZE", "bog": "BOG",
  "mde": "MDE", "ctg": "CTG", "lim": "LIM", "cuz": "CUZ",
  "gig": "GIG", "gru": "GRU", "scl": "SCL", "bkk": "BKK",
  "ist": "IST",
  // Multi-city V1 — Japan
  "kyo": "KYO", "osa": "OSA", "hkn": "HKN", "knz": "KNZ",
  // Multi-city V1 — Spain / Andalusia
  "sev": "SEV", "gra": "GRA", "cor": "COR", "agp": "AGP",
  "cdz": "CDZ", "tol": "TOL",
  // Multi-city V1 — Portugal
  "opo": "OPO", "cbm": "CBM", "fao": "FAO", "dov": "DOV",
  // Multi-city V1 — Benelux
  "bge": "BGE",
  // Multi-city V1 — Italy
  "rom": "ROM", "vce": "VCE", "nap": "NAP",

  // ── English names & slugs ──
  "tokyo":                  "TYO",
  "barcelona":              "BCN",
  "lisbon":                 "LIS",
  "paris":                  "CDG",
  "madrid":                 "MAD",
  "rome":                   "FCO",
  "london":                 "LHR",
  "amsterdam":              "AMS",
  "berlin":                 "BER",
  "milan":                  "MXP",
  "milano":                 "MXP",
  "florence":               "FLR",
  "firenze":                "FLR",
  "new york":               "NYC",
  "new-york":               "NYC",
  "newyork":                "NYC",
  "miami":                  "MIA",
  "buenos aires":           "EZE",
  "buenos-aires":           "EZE",
  "bogota":                 "BOG",
  "medellin":               "MDE",
  "cartagena":              "CTG",
  "lima":                   "LIM",
  "cusco":                  "CUZ",
  "cuzco":                  "CUZ",
  "rio de janeiro":         "GIG",
  "rio-de-janeiro":         "GIG",
  "rio":                    "GIG",
  "sao paulo":              "GRU",
  "sao-paulo":              "GRU",
  "santiago":               "SCL",
  "santiago de chile":      "SCL",
  "bangkok":                "BKK",
  "istanbul":               "IST",
  "mexico city":            "MEX",
  "cdmx":                   "MEX",
  "ciudad de mexico":       "MEX",

  // ── Spanish names ──
  "tokio":                  "TYO",
  "lisboa":                 "LIS",
  "parís":                  "CDG",
  "roma":                   "ROM",
  "londres":                "LHR",
  "ámsterdam":              "AMS",
  "berlín":                 "BER",
  "milán":                  "MXP",
  "florencia":              "FLR",
  "nueva york":             "NYC",
  "bogotá":                 "BOG",
  "medellín":               "MDE",
  "río de janeiro":         "GIG",
  "río-de-janeiro":         "GIG",
  "río":                    "GIG",
  "são paulo":              "GRU",
  "estambul":               "IST",
  "ciudad de méxico":       "MEX",
  // Multi-city V1
  "kioto":                  "KYO",
  "kyoto":                  "KYO",
  "osaka":                  "OSA",
  "hakone":                 "HKN",
  "kanazawa":               "KNZ",
  "sevilla":                "SEV",
  "seville":                "SEV",
  "granada":                "GRA",
  "córdoba":                "COR",
  "cordoba":                "COR",
  "málaga":                 "AGP",
  "malaga":                 "AGP",
  "cádiz":                  "CDZ",
  "cadiz":                  "CDZ",
  "toledo":                 "TOL",
  "oporto":                 "OPO",
  "porto":                  "OPO",
  "coimbra":                "CBM",
  "faro":                   "FAO",
  "douro":                  "DOV",
  "valle del duero":        "DOV",
  "brujas":                 "BGE",
  "bruges":                 "BGE",
  "venecia":                "VCE",
  "venice":                 "VCE",
  "venezia":                "VCE",
  "nápoles":                "NAP",
  "naples":                 "NAP",
  "napoli":                 "NAP",
};

// ─── Category alias map ───────────────────────────────────────────────────────
// Maps Spanish labels and common synonyms to canonical category keys.

const CATEGORY_ALIAS_MAP: Readonly<Record<string, string>> = {
  // food
  "gastronomia":    "food", "gastronomía":   "food", "comida":        "food",
  "restaurante":    "food", "restaurant":    "food", "foodie":        "food",
  "comer":          "food",
  // nightlife
  "vida nocturna":  "nightlife", "noche":     "nightlife", "nocturno": "nightlife",
  // culture
  "cultura":        "culture",
  // art
  "arte":           "art",
  // nature
  "naturaleza":     "nature",
  // music
  "musica":         "music",   "música":      "music",
  // architecture
  "arquitectura":   "architecture",
  // wellness
  "bienestar":      "wellness", "spa":        "wellness", "yoga":      "wellness",
  "relax":          "wellness",
  // shopping
  "compras":        "shopping",
  // romantic
  "romantico":      "romantic", "romántico":  "romantic", "romance":   "romantic",
  "pareja":         "romantic",
  // markets
  "mercado":        "markets",  "mercados":   "markets",  "market":    "markets",
  // cafes
  "cafe":           "cafes",    "café":       "cafes",    "cafés":     "cafes",
  "cafeteria":      "cafes",    "coffee":     "cafes",
  // family
  "familia":        "family",   "ninos":      "family",   "niños":     "family",
  // hidden_gems
  "hidden gems":    "hidden_gems", "joyas ocultas": "hidden_gems",
  "lugares ocultos":"hidden_gems",
  // viewpoints
  "miradores":      "viewpoints", "mirador":  "viewpoints",
  // local_life
  "local life":     "local_life", "vida local": "local_life",
  // design
  "diseño":         "design",   "diseno":     "design",
  // history
  "historia":       "history",  "historico":  "history",  "histórico": "history",
  // sports
  "deporte":        "sports",   "deportes":   "sports",   "sport":     "sports",
};

// ─── Public helpers ───────────────────────────────────────────────────────────

/**
 * Resolve any city input to a CityVisual.
 *
 * Accepts (all case-insensitive):
 *   • IATA code          — "TYO", "mad", "CDG"
 *   • URL slug           — "new-york", "sao-paulo", "buenos-aires"
 *   • English city name  — "Tokyo", "Florence", "Istanbul"
 *   • Spanish city name  — "Tokio", "Florencia", "Estambul"
 *
 * Returns null when the city is not in the curated registry.
 */
export function getCityVisual(cityOrIata: string): CityVisual | null {
  if (!cityOrIata) return null;
  const key  = cityOrIata.toLowerCase().trim();
  const iata = CITY_SLUG_TO_IATA[key] ?? key.toUpperCase();
  return CITY_VISUALS[iata] ?? null;
}

/**
 * Resolve a category string to a CategoryVisual.
 *
 * Accepts canonical keys ("food", "nightlife", …) and common
 * Spanish aliases ("gastronomía", "vida nocturna", …).
 *
 * Returns null when the category has no curated image.
 */
export function getCategoryVisual(category: string): CategoryVisual | null {
  if (!category) return null;
  const key      = category.toLowerCase().trim();
  const canonical = CATEGORY_ALIAS_MAP[key] ?? key;
  return CATEGORY_VISUALS[canonical] ?? null;
}

// ─── Category fallback pools (Marketplace Image Quality V2 / V3) ─────────────
// A small curated pool of alternates for high-frequency marketplace
// categories — culture, food and nature especially were the ones a 30-
// destination audit (V3) found repeating too often (Bali/nature, Porto/food,
// Vienna/culture all showed the same single image on nearly every card).
// Categories not listed here fall back to their single CATEGORY_VISUALS
// image — same backward-compatible value as before, just wrapped in a
// one-item array so callers have one shape to deal with.
export const CATEGORY_VISUAL_POOLS: Readonly<Record<string, readonly string[]>> = {
  culture: [
    CATEGORY_VISUALS.culture.image,
    CATEGORY_VISUALS.history.image,
    unsplashUrl("1763934385931-8888d883fe3c", 400, 400), // colorful street mural
  ],
  art: [
    CATEGORY_VISUALS.art.image,
    unsplashUrl("1763934385931-8888d883fe3c", 400, 400), // colorful street mural
  ],
  food: [
    CATEGORY_VISUALS.food.image,
    unsplashUrl("1646296586390-723f37ba380a", 400, 400), // wine + cheese board
    CATEGORY_VISUALS.markets.image,
  ],
  nature: [
    CATEGORY_VISUALS.nature.image,
    unsplashUrl("1723764429065-ee9fe446990b", 400, 400), // mountain hiking trail
  ],
  architecture: [
    CATEGORY_VISUALS.architecture.image,
    unsplashUrl("1760081848995-ee37cd4e9553", 400, 400), // symmetrical facade detail
  ],
  nightlife: [
    CATEGORY_VISUALS.nightlife.image,
    unsplashUrl("1574101412892-7e945f20b282", 400, 400), // jazz club neon sign
  ],
  music: [
    CATEGORY_VISUALS.music.image,
    unsplashUrl("1722570014488-7ec7d5b1af31", 400, 400), // guitarist on stage
  ],
  shopping: [
    CATEGORY_VISUALS.shopping.image,
    unsplashUrl("1777892505710-94b77d212d19", 400, 400), // flea market stalls
  ],
  markets: [
    CATEGORY_VISUALS.markets.image,
    unsplashUrl("1777892505710-94b77d212d19", 400, 400), // flea market stalls
  ],
};

/**
 * Resolve a category/intent key to its fallback image pool (1+ URLs).
 * Accepts the same canonical keys and Spanish aliases as getCategoryVisual.
 * Returns an empty array when the category has no curated image at all.
 */
export function getCategoryVisualPool(category: string): readonly string[] {
  if (!category) return [];
  const key       = category.toLowerCase().trim();
  const canonical = CATEGORY_ALIAS_MAP[key] ?? key;
  const pool = CATEGORY_VISUAL_POOLS[canonical];
  if (pool && pool.length > 0) return pool;
  const single = CATEGORY_VISUALS[canonical];
  return single ? [single.image] : [];
}

// ─── Visual intent detection (Marketplace Image Quality V2 / V3) ─────────────
// Corrects the fallback image for experiences whose *title* signals a much
// more specific visual identity than their backend category — e.g. a
// Santiago Bernabéu stadium tour is commonly tagged category="culture", but
// must never render a generic museum/culture image. Keyword-based and
// generalizable across any city, team, or museum — never a per-city special
// case; the same table applies identically to Madrid, Barcelona, Milan, etc.
// Only ever used for image/alt-text selection — never changes exp.category
// or any other backend-sourced field.
//
// Checked in this order (first match wins) so more specific buckets
// (shopping's "flea"/"rastro"/"artesanía") outrank more generic ones
// ("mercado" in food) when a title could plausibly match both.
const VISUAL_INTENT_KEYWORDS: ReadonlyArray<readonly [string, readonly string[]]> = [
  ["stadium", [
    "bernabéu", "bernabeu", "estadio", "stadium", "arena",
    "real madrid", "camp nou", "san siro", "wembley", "allianz arena",
    "old trafford", "maracanã", "maracana", "emirates stadium", "etihad",
    "signal iduna", "juventus stadium",
  ]],
  ["shopping", [
    "shopping", "boutique", "boutiques", "craft", "artesanía", "artesania",
    "flea", "rastro",
  ]],
  ["culture", [
    "museo", "museum", "galería", "galeria", "gallery", "pinacoteca", "arte", "mural",
  ]],
  ["food", [
    "mercado", "market", "tapas", "comida", "food", "gastronomía", "gastronomia",
    "wine", "vino", "winery", "wineries", "street food", "food tour",
  ]],
  ["nightlife", [
    "pub", "pubs", "bar", "bars", "jazz", "club", "clubs", "nightlife",
    "música", "musica", "concert", "concierto",
  ]],
  ["nature", [
    "park", "parks", "parque", "beach", "beaches", "playa", "trail", "trails",
    "hike", "montaña", "montana", "jardín", "jardin",
  ]],
];

/** Escapes regex metacharacters so a keyword is matched as a literal string. */
function escapeRegExpLiteral(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Whole-word / whole-phrase match, accent-sensitive-safe and case-insensitive
 * — critical once short, common words like "bar", "club" or "park" are in
 * play: a naive substring check would misfire on "Barcelona" or "Parking".
 */
function containsKeyword(haystack: string, keyword: string): boolean {
  const pattern = new RegExp(`(^|[^\\p{L}\\p{N}])${escapeRegExpLiteral(keyword)}([^\\p{L}\\p{N}]|$)`, "iu");
  return pattern.test(haystack);
}

/**
 * Detects a visual intent from an experience's title (and, when known, its
 * neighborhood) — independent of its backend category. Returns null when
 * nothing matches, in which case callers should fall back to the category.
 * Deterministic: pure string matching, no randomness.
 */
export function resolveVisualIntent(title: string, neighborhood?: string | null): string | null {
  const haystack = `${title ?? ""} ${neighborhood ?? ""}`;
  for (const [intent, keywords] of VISUAL_INTENT_KEYWORDS) {
    if (keywords.some(k => containsKeyword(haystack, k))) return intent;
  }
  return null;
}
